import { Link } from "react-router-dom";
import { Compass, Heart, Leaf, Users, Award, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { stats } from "../lib/mock";

export default function About() {
  return (
    <main className="pt-24 sm:pt-32 pb-16 paper-texture">
      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-14 sm:mb-20">
        <div className="flex justify-center mb-4 sm:mb-6"><span className="leaf-divider">Our story</span></div>
        <h1 className="font-serif text-3xl sm:text-5xl md:text-7xl font-bold text-[#1F4D3A] leading-[1.1] mb-4 sm:mb-6">
          Four cousins.<br />One <em className="text-[#C87A50] not-italic">road</em>. A thousand memories.
        </h1>
        <p className="text-base sm:text-lg md:text-xl text-[#4A3728] leading-relaxed max-w-3xl mx-auto">
          On Wheels began as a family road trip that quietly turned into a life's work — sharing hidden trails, mountains, waterfalls and slow travel with people who love the road as much as we do.
        </p>
      </section>

      {/* OUR STORY — emotional narrative + road-trip & Jog Falls imagery */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-20 sm:mb-24">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 sm:gap-10 items-center">
          {/* Image mosaic \u2014 left on desktop */}
          <div className="lg:col-span-2 grid grid-cols-2 gap-3 sm:gap-4">
            <div className="col-span-2 aspect-[16/10] rounded-2xl overflow-hidden img-zoom shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1504598318550-17eba1008a68?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
                alt="Four cousins on a road trip through mountains"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="aspect-square rounded-2xl overflow-hidden img-zoom shadow-md relative">
              <img
                src="https://images.pexels.com/photos/9817689/pexels-photo-9817689.jpeg"
                alt="Jog Falls in full monsoon flow"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-2 left-2 right-2 text-white text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded bg-black/40 backdrop-blur text-center">
                Jog Falls, Karnataka
              </div>
            </div>
            <div className="aspect-square rounded-2xl overflow-hidden img-zoom shadow-md">
              <img
                src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Open road stretching into the horizon"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Story text \u2014 right on desktop */}
          <div className="lg:col-span-3">
            <span className="leaf-divider inline-flex">Our story</span>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-[#1F4D3A] leading-[1.1] mt-4 mb-6">
              Four Cousins. One Road.<br />
              <em className="text-[#C87A50] not-italic">A Thousand Memories.</em>
            </h2>

            <div className="space-y-5 text-base sm:text-lg text-[#4A3728] leading-relaxed font-serif">
              <p>
                It started with four cousins, one road trip and a simple idea — to escape the routine and discover places beyond the usual tourist paths.
              </p>
              <p>
                What began as a journey between four people slowly became something much bigger. We shared long rides, unexpected turns, mountain roads, waterfalls, campfires, laughter and countless memories. Somewhere along those roads, we realised that the best part of travelling isn't just reaching a destination — it's the people, experiences and stories we collect along the way.
              </p>
              <p className="text-[#1F4D3A] italic border-l-4 border-[#C87A50] pl-4 sm:pl-5">
                That one road trip gave us a dream: to create journeys for others that feel just as real, adventurous and unforgettable.
              </p>
              <p>
                And that's how our journey began.
              </p>
              <p>
                Today, we bring together hidden trails, mountains, waterfalls, beaches, heritage, culture and adventure into thoughtfully planned trips — so you can travel less like a tourist and more like an explorer.
              </p>
              <p className="font-sans text-[#1F4D3A] font-semibold not-italic">
                We started with four cousins. Now, we want <span className="text-[#C87A50]">you</span> to be part of our journey.
              </p>
            </div>

            <div className="mt-8">
              <Link to="/tours">
                <Button size="lg" className="btn-primary bg-[#C87A50] hover:bg-[#B4653A] text-white rounded-full px-8 h-12">
                  Explore our journeys <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="max-w-6xl mx-auto px-6 lg:px-8 mb-24">
        <div className="text-center mb-14">
          <span className="leaf-divider inline-flex">Why we exist</span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#1F4D3A] mt-4">The four things we hold sacred.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            { icon: Heart, title: 'Kindness on the road', desc: 'A good trip is a kind one. We pay hosts fairly, tip generously and never haggle for the sake of it.' },
            { icon: Leaf, title: 'Leave-no-trace', desc: 'Reusable bottles for every guest, waste segregated at every camp, and zero single-use plastic since 2020.' },
            { icon: Users, title: 'Small groups only', desc: 'A maximum of sixteen travellers per departure so every homestay stays a home, and every conversation counts.' },
            { icon: Award, title: 'Craft over calendar', desc: 'We’d rather run twelve careful trips a year than a hundred cookie-cutter ones. Slow travel needs slow planning.' }
          ].map((v) => (
            <div key={v.title} className="bg-white rounded-2xl p-8 border border-[#E5DDCB] card-lift">
              <div className="w-12 h-12 rounded-full bg-[#F5F0E8] flex items-center justify-center mb-4">
                <v.icon className="w-5 h-5 text-[#1F4D3A]" />
              </div>
              <h3 className="font-serif text-2xl font-semibold text-[#1F4D3A] mb-2">{v.title}</h3>
              <p className="text-[#5A4A38] leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Stats band */}
      <section className="bg-[#1F4D3A] py-16 mb-24">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 px-6 text-center">
          {stats.map((s) => (
            <div key={s.label}>
              <div className="font-serif text-4xl md:text-5xl font-bold text-[#C9A961]">{s.value}</div>
              <div className="text-xs md:text-sm text-[#D9CDB0] uppercase tracking-widest mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Mission — replaces the previous team grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
        <div className="rounded-3xl bg-gradient-to-br from-[#1F4D3A] via-[#245844] to-[#2D6B4F] text-[#F5F0E8] px-6 sm:px-10 md:px-16 py-14 sm:py-16 relative overflow-hidden">
          <div aria-hidden="true" className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#C9A961]/10 blur-3xl" />
          <div aria-hidden="true" className="absolute -bottom-20 -left-16 w-72 h-72 rounded-full bg-[#C87A50]/10 blur-3xl" />

          <div className="relative grid grid-cols-1 md:grid-cols-5 gap-10 items-start">
            <div className="md:col-span-2">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#C9A961] font-semibold">
                <Compass className="w-4 h-4" /> Our mission
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold mt-4 leading-[1.1]">
                Travel that feels like a <em className="text-[#C9A961] not-italic">story</em>, not a checklist.
              </h2>
            </div>

            <div className="md:col-span-3 space-y-5 text-base sm:text-lg text-[#E8DFC7] leading-relaxed">
              <p>
                We build small, thoughtful road trips that turn strangers into a tribe, roads into memories and passing landscapes into moments you carry home.
              </p>
              <p>
                Every itinerary is designed around what actually makes a journey unforgettable — a homestay run by a family, a sunrise you had to earn, a plate of food you'll dream about, a road no map will suggest.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
                <div className="rounded-2xl bg-white/5 border border-white/10 p-5" data-testid="mission-pillar-explore">
                  <div className="text-[#C9A961] font-serif text-2xl font-bold">01</div>
                  <h4 className="font-serif text-lg font-semibold mt-1">Explore boldly</h4>
                  <p className="text-sm text-[#D9CDB0] mt-2">Off the tourist trail, into the real India — waterfalls, forts, forests and fishing villages.</p>
                </div>
                <div className="rounded-2xl bg-white/5 border border-white/10 p-5" data-testid="mission-pillar-travel">
                  <div className="text-[#C9A961] font-serif text-2xl font-bold">02</div>
                  <h4 className="font-serif text-lg font-semibold mt-1">Travel gently</h4>
                  <p className="text-sm text-[#D9CDB0] mt-2">Small groups, local hosts, fair pay, zero single-use plastic. The road stays as we found it.</p>
                </div>
                <div className="rounded-2xl bg-white/5 border border-white/10 p-5" data-testid="mission-pillar-belong">
                  <div className="text-[#C9A961] font-serif text-2xl font-bold">03</div>
                  <h4 className="font-serif text-lg font-semibold mt-1">Belong together</h4>
                  <p className="text-sm text-[#D9CDB0] mt-2">You arrive as a guest, leave as family. Every trip ends with a group chat that never dies.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <Compass className="w-12 h-12 text-[#C87A50] mx-auto mb-4" />
        <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#1F4D3A] mb-4">Come travel with us.</h2>
        <p className="text-lg text-[#4A3728] mb-8">A quiet homestay in Kaza, a beachside bonfire in Palolem, a fossil hunt in Langza — pick a story.</p>
        <Link to="/tours">
          <Button size="lg" className="btn-primary bg-[#1F4D3A] hover:bg-[#2D6B4F] text-white rounded-full px-10 h-14">
            Explore journeys <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </section>
    </main>
  );
}
